import type { Game } from '../core/game';
import { BALANCE } from '../data/balance';
import { callGame, nodeLevel } from './gameApi';

/**
 * Adaptateur « Matériaux » — v0.47.0 (A95, lot B : écrans du mode infini, lot 2).
 *
 * Même contrat que `ui/abyssApi.ts` : **rien** de ce que livre le lot A (cœur)
 * n'est supposé présent. `game.materialBonuses()`, `state.combat.woodUsed`,
 * `overkillCarry`, `lastTotal`, `lastBasicSum`, `lastMasterFaces`,
 * `enemy.broken`, `enemy.effectVoided` et `breakdown.ironBasicMult` sont lus
 * par des fonctions gardées qui rendent une valeur neutre quand la donnée
 * manque : sur un cœur qui ne livre pas encore les bonus, aucune puce de
 * Matériau n'apparaît et les cartes de dés ne changent pas.
 *
 * Module sans DOM, sans lien statique vers un type du cœur non livré.
 */

export const MATERIOLOGY_NODE = 'aby_materiology';

type Rec = Record<string, unknown>;

function rec(v: unknown): Rec {
  return typeof v === 'object' && v !== null ? (v as Rec) : {};
}

function finite(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function balanceNumber(key: string, fallback: number): number {
  return finite((BALANCE as unknown as Rec)[key]) ?? fallback;
}

// ───────────────────────────────────────────────────── Bonus actifs (cœur)

/** Un bonus de Matériau actif (`Game.materialBonuses()`). */
export interface MaterialBonusView {
  material: string;
  name: string;
  /** Nombre de pièces du palier : 3 (nœud *Matériau*) ou 5 (*Matériologie*). */
  tier: 3 | 5;
}

/** `game.materialBonuses()` nettoyé ; liste vide sans le cœur ou en cas d'erreur. */
export function materialBonusesOf(game: Game): MaterialBonusView[] {
  const raw = callGame(game, 'materialBonuses');
  if (!Array.isArray(raw)) return [];
  const out: MaterialBonusView[] = [];
  for (const item of raw) {
    const o = rec(item);
    const tier = finite(o.tier);
    if (typeof o.material !== 'string' || (tier !== 3 && tier !== 5)) continue;
    out.push({ material: o.material, name: typeof o.name === 'string' ? o.name : o.material, tier });
  }
  return out;
}

// ───────────────────────────────────────────── Constantes d'équilibrage

/** Chiffres des bonus, lus dans `BALANCE` avec le repli du contrat A95 §1. */
export interface MaterialConstants {
  /** Boisé 3 : secondes ajoutées (`woodInvasionSeconds`, 10). */
  woodSeconds: number;
  /** Osseux 3 : multiplicateur d'un monstre brisé (`boneBrokenMultiplier`, 1,5). */
  boneBase: number;
  /** Osseux 5 : ajout par bris supplémentaire (`boneBrokenStep`, 0,5). */
  boneStep: number;
  /** Ferreux 3 : facteur des dés Basiques (`ironBasicMultiplier`, 1,25). */
  ironBase: number;
  /** Caoutchouc : emplacements qui rebondissent à 3 et à 5 pièces (1 et 3). */
  rubberSmall: number;
  rubberLarge: number;
}

export function materialConstants(): MaterialConstants {
  return {
    woodSeconds: balanceNumber('woodInvasionSeconds', 10),
    boneBase: balanceNumber('boneBrokenMultiplier', 1.5),
    boneStep: balanceNumber('boneBrokenStep', 0.5),
    ironBase: balanceNumber('ironBasicMultiplier', 1.25),
    rubberSmall: balanceNumber('rubberSlotsSmall', 1),
    rubberLarge: balanceNumber('rubberSlotsLarge', 3),
  };
}

// ─────────────────────────────────────────── État de combat des Matériaux

/** Ce que les puces lisent dans l'état de combat (valeurs neutres sans le cœur). */
export interface MaterialRuntime {
  /** Boisé 3 : le +10 s de l'Invasion a été utilisé. */
  woodUsed: boolean;
  /** Boisé 5 : somme des Basiques du lancer précédent (0 au départ). */
  lastBasicSum: number;
  /** Du Vide 5 : total du lancer précédent (0 au départ). */
  lastTotal: number;
  /** Silicium 3 : excédent en attente pour le prochain lancer. */
  overkillCarry: number;
  /** Silicium 5 : nombre de faces Maîtres mémorisées pour le dé fantôme. */
  ghostFaces: number;
  /** Osseux : nombre de bris de l'ennemi courant (0 = intact). */
  broken: number;
  /** Du Vide 3 : les effets de l'ennemi courant sont annulés. */
  effectVoided: boolean;
  /** Ferreux : `ironBasicMult` du dernier lancer (1 sans effet). */
  ironMult: number;
}

/** `breakdown` du dernier lancer du combat (journal `state.combat.throws`), ou `{}`. */
export function lastBreakdownOf(game: Game): Rec {
  const throws = rec(rec(game.state).combat).throws;
  if (!Array.isArray(throws) || throws.length === 0) return {};
  return rec(rec(rec(throws[throws.length - 1]).result).breakdown);
}

export function readMaterialRuntime(game: Game): MaterialRuntime {
  const combat = rec(rec(game.state).combat);
  const enemy = rec(rec(game.state).enemy);
  const faces = combat.lastMasterFaces;
  const iron = finite(lastBreakdownOf(game).ironBasicMult);
  return {
    woodUsed: combat.woodUsed === true,
    lastBasicSum: Math.max(0, finite(combat.lastBasicSum) ?? 0),
    lastTotal: Math.max(0, finite(combat.lastTotal) ?? 0),
    overkillCarry: Math.max(0, finite(combat.overkillCarry) ?? 0),
    ghostFaces: Array.isArray(faces) ? faces.length : 0,
    broken: Math.max(0, Math.floor(finite(enemy.broken) ?? 0)),
    effectVoided: enemy.effectVoided === true,
    ironMult: iron !== null && iron > 0 ? iron : 1,
  };
}

/** Tout ce dont `materialChips` (hudTalents) a besoin. */
export interface MaterialFacts {
  /** Paliers actifs : `{ material, tier }`. */
  bonuses: { material: string; tier: number }[];
  runtime: MaterialRuntime;
  constants: MaterialConstants;
}

export function readMaterialFacts(game: Game): MaterialFacts {
  return {
    bonuses: materialBonusesOf(game),
    runtime: readMaterialRuntime(game),
    constants: materialConstants(),
  };
}

// ───────────────────────────────────────────────── Pièces d'un Matériau

/** Dés Maîtres **équipés** portant ce Matériau (A95-1 : les pièces sont les équipés). */
export function equippedMaterialCount(game: Game, material: string): number {
  const state = rec(game.state);
  const equipped = Array.isArray(state.equipped) ? (state.equipped as unknown[]) : [];
  const inventory = Array.isArray(state.inventory) ? (state.inventory as unknown[]) : [];
  let count = 0;
  for (const id of equipped) {
    if (typeof id !== 'string') continue;
    const die = inventory.find((d) => rec(d).id === id);
    if (die && rec(die).material === material) count += 1;
  }
  return count;
}

/**
 * Avancement des pièces d'un Matériau pour la carte et la fiche d'un dé :
 * « 3 pièces : actif » quand le cœur annonce le bonus, sinon « 2 / 3 » (compte
 * local des dés équipés) ; le palier de 5 pièces s'ajoute avec *Matériologie* :
 * « 3 pièces : actif · 4 / 5 », « 5 pièces : actif ». Sans `materialBonuses()`,
 * seul le compte est dit (jamais « actif » : le cœur n'a rien annoncé).
 */
export function materialSetTexts(game: Game, material: string): string[] {
  const count = equippedMaterialCount(game, material);
  const mine = materialBonusesOf(game).filter((b) => b.material === material);
  const out: string[] = [];
  out.push(mine.some((b) => b.tier === 3) ? '3 pièces : actif' : `${Math.min(count, 3)} / 3`);
  const materiology = nodeLevel(game, MATERIOLOGY_NODE) >= 1;
  if (mine.some((b) => b.tier === 5)) out.push('5 pièces : actif');
  else if (materiology) out.push(`${Math.min(count, 5)} / 5`);
  return out;
}
